import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";

import PageWrapper from "../components/PageWrapper";
import Instructions from "../components/Instructions/Instructions";
import ModuleHero from "../sections/academy/ModuleHero";
import SampleVideo from "../sections/academy/SampleVideo";
import LessonList from "../sections/academy/LessonList";
import AiMentor from "../sections/academy/AiMentor";
import EnrollCta from "../sections/academy/EnrollCta";
import { trackEvent } from "../lib/elearning";

const Layout = styled.div`
  background: var(--nd-black);
`;

const Grid = styled.div`
  padding: 48px 0 96px;
  @media (max-width: 767px) { padding-bottom: 96px; /* leave room for sticky CTA */ }
`;

const MobileMentorButton = styled.button`
  display: none;
  @media (max-width: 991px) {
    display: inline-flex;
    position: fixed;
    bottom: 88px;
    right: 16px;
    z-index: 70;
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    background: var(--nd-surface);
    color: var(--nd-text-display);
    border: 1px solid var(--nd-border-visible);
    border-radius: 999px;
    padding: 10px 16px;
    min-height: 44px;
    cursor: pointer;
  }
`;

const ElearningModulePage = ({ pageContext }) => {
  const { module } = pageContext;
  const [mentorOpen, setMentorOpen] = useState(false);

  useEffect(() => {
    trackEvent("course_view", { module_id: module.id, module_title: module.title });
  }, [module.id, module.title]);

  return (
    <PageWrapper>
      <Helmet>
        <title>{module.title} — Dioscuro Academy</title>
        <meta name="description" content={module.description} />
        <meta property="og:title" content={`${module.title} — Dioscuro Academy`} />
        <meta property="og:description" content={module.description} />
        {module.ogImage && <meta property="og:image" content={module.ogImage} />}
      </Helmet>
      <Layout>
        <ModuleHero module={module} />
        <SampleVideo
          url={module.sampleVideoUrl}
          title={module.title}
          onPlay={() => trackEvent("lesson_play", { module_id: module.id, lesson_id: "preview" })}
        />
        <Grid>
          <Container>
            <Instructions label="how to use this course">
              <h4>What's here</h4>
              <p>
                A short, opinionated course on {module.title}. Each lesson
                runs 5–15 minutes. The free preview is unlocked above.
              </p>
              <h4>How to learn</h4>
              <ol>
                <li>Watch the preview video to gauge the pace.</li>
                <li>Open the lesson list — start with lesson 1 unless you already know it.</li>
                <li>
                  Ask the AI mentor (right panel on desktop, "Ask mentor"
                  button on mobile) any question scoped to this module —
                  it answers from the curriculum, not the open web.
                </li>
                <li>
                  Click <strong>Enroll</strong> at the bottom to track
                  progress. Earns XP on each lesson completed.
                </li>
              </ol>
              <h4>Pair with hands-on labs</h4>
              <p>
                Course modules map to lab packs. After watching, head to{" "}
                <a href="/labs">DevOps Labs</a> to build the same systems.
              </p>
            </Instructions>
            <Row>
              <Col lg="8">
                <LessonList lessons={module.lessons} moduleId={module.id} />
                <div style={{ marginTop: 32 }}>
                  <EnrollCta moduleId={module.id} moduleTitle={module.title} />
                </div>
              </Col>
              <Col lg="4">
                <AiMentor
                  moduleId={module.id}
                  moduleTitle={module.title}
                  open={mentorOpen}
                  onClose={() => setMentorOpen(false)}
                />
              </Col>
            </Row>
          </Container>
        </Grid>
        <MobileMentorButton type="button" onClick={() => setMentorOpen(true)}>
          Ask mentor
        </MobileMentorButton>
      </Layout>
    </PageWrapper>
  );
};

export default ElearningModulePage;
